import { useState, useRef, useEffect } from 'react';
import { BrandEditor, defaultBrandListState, type BrandListState } from './BrandEditor';
import type { CreateBrandSeed, EditBrandPatchSeed } from './brandSeeds';
import { ReferenceBrowser } from './ReferenceBrowser';
import { ReferenceEditor } from './ReferenceEditor';
import { BackfillProductUrls } from './BackfillProductUrls';
import { createWatchReference, normalizeReference, getWatchReference, getWatchBrandById, computeSearchText } from '../../services/firebase';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import type { WatchBrand, WatchReference } from '../../types';
import toast from 'react-hot-toast';

type View = 'brands' | 'references' | 'editor';

// Helper to recursively remove undefined values from objects (Firestore rejects undefined)
function removeUndefined<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined) as T;
  }
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (value !== undefined) {
        result[key] = removeUndefined(value);
      }
    }
    return result as T;
  }
  return obj;
}

interface CuratedWatchesProps {
  /** Open the cross-brand Extractor Jobs screen, pre-filtered to a brand. */
  onOpenExtractorJobs?: (brandId: string) => void;
  onOpenBulkImport?: (brandId: string) => void;
  // When set (e.g. on returning from Import Sources via "Back to brand refs"),
  // open this brand's references view on mount instead of the brands list.
  initialBrandId?: string;
  onInitialBrandConsumed?: () => void;
  // When set (e.g. from the Reference Cleanup large-cluster warning), open this
  // brand's EDIT modal on the brands list (where the Dedup identity setting is).
  editBrandId?: string;
  onEditBrandConsumed?: () => void;
  // When both set (e.g. from an AI Sandbox match), open this reference's editor
  // directly. brandId is required because a ref lives in a brand subcollection.
  openReferenceBrandId?: string;
  openReferenceId?: string;
  onOpenReferenceConsumed?: () => void;
  /**
   * Ask the references listing to start a crawl once it opens — set by the
   * extractor config screen's Open action, alongside `initialBrandId`. Passed
   * straight through; `ReferenceBrowser` owns the decision and the timing.
   */
  autoRunScraper?: boolean;
  onAutoRunScraperConsumed?: () => void;
  /**
   * One-shot seeds from an external-catalogue review (External Brand
   * Proposals): open the brand Create modal pre-filled, or the Edit modal with
   * values overlaid. Passed straight through — `BrandEditor` owns the modals,
   * the consume-once handling and the write. `…Applied` fires only once the
   * brand has actually been written.
   */
  createBrandSeed?: CreateBrandSeed | null;
  onCreateBrandSeedConsumed?: () => void;
  onCreateBrandSeedApplied?: (brandId: string, seed: CreateBrandSeed) => void;
  editBrandPatchSeed?: EditBrandPatchSeed | null;
  onEditBrandPatchSeedConsumed?: () => void;
  onEditBrandPatchSeedApplied?: (brandId: string, seed: EditBrandPatchSeed) => void;
  /** A seeded modal was closed without saving. */
  onBrandSeedCancelled?: (seed: CreateBrandSeed | EditBrandPatchSeed) => void;
}

export function CuratedWatches({ onOpenExtractorJobs, onOpenBulkImport, initialBrandId, onInitialBrandConsumed, editBrandId, onEditBrandConsumed, openReferenceBrandId, openReferenceId, onOpenReferenceConsumed, autoRunScraper, onAutoRunScraperConsumed, createBrandSeed, onCreateBrandSeedConsumed, onCreateBrandSeedApplied, editBrandPatchSeed, onEditBrandPatchSeedConsumed, onEditBrandPatchSeedApplied, onBrandSeedCancelled }: CuratedWatchesProps = {}) {
  const [view, setView] = useState<View>('brands');
  const [selectedBrand, setSelectedBrand] = useState<WatchBrand | null>(null);
  const [selectedReference, setSelectedReference] = useState<WatchReference | null>(null);
  // The parent a variant was opened from, so the breadcrumb can offer a way back
  // up. Set only when jumping parent → variant inside the editor; cleared on any
  // other navigation (including variant → its own parent).
  const [variantParent, setVariantParent] = useState<WatchReference | null>(null);
  const [brandListState, setBrandListState] = useState<BrandListState>(defaultBrandListState);
  // Bridge to the editor's unsaved-changes guard so the breadcrumb links
  // ("Brands" / brand name) route through the confirm dialog while the edit is
  // dirty. Only set while the editor view is mounted; null otherwise.
  const editorExitGuardRef = useRef<((proceed: () => void) => void) | null>(
    null,
  );
  const guardedExit = (proceed: () => void) => {
    const guard = editorExitGuardRef.current;
    if (guard) guard(proceed);
    else proceed();
  };

  // Brand edit from reference view
  const [showBrandEdit, setShowBrandEdit] = useState(false);

  // Clone modal state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [pendingCloneData, setPendingCloneData] = useState<Partial<WatchReference> | null>(null);
  const [cloneReference, setCloneReference] = useState('');
  const [cloneModel, setCloneModel] = useState('');
  const [cloneDiameterMm, setCloneDiameterMm] = useState('');
  const [cloneCaseMaterial, setCloneCaseMaterial] = useState('');
  const [cloneStartYear, setCloneStartYear] = useState('');
  const [cloneEndYear, setCloneEndYear] = useState('');
  const [isCloning, setIsCloning] = useState(false);

  const handleSelectBrand = (brand: WatchBrand) => {
    setSelectedBrand(brand);
    setView('references');
  };

  const handleBackToBrands = () => {
    setSelectedBrand(null);
    setSelectedReference(null);
    setVariantParent(null);
    setView('brands');
  };

  // Returning from Import Sources with a brand origin: jump straight into that
  // brand's references view. Fetch the brand fresh (CuratedWatches unmounts when
  // the Import Sources tab is active, so there's no retained selection to reuse).
  useEffect(() => {
    if (!initialBrandId) return;
    let cancelled = false;
    (async () => {
      try {
        const brand = await getWatchBrandById(initialBrandId);
        if (cancelled) return;
        if (brand) {
          setSelectedBrand(brand);
          setSelectedReference(null);
          setView('references');
        }
      } catch (err) {
        console.error('Failed to load brand for references view:', err);
      } finally {
        if (!cancelled) onInitialBrandConsumed?.();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBrandId]);

  // Deep-linked from the Reference Cleanup large-cluster warning: open the
  // brand's edit modal directly on the brands list so the admin can flip the
  // Dedup identity setting. Fetch fresh (this tab unmounts when inactive).
  useEffect(() => {
    if (!editBrandId) return;
    let cancelled = false;
    (async () => {
      try {
        const brand = await getWatchBrandById(editBrandId);
        if (cancelled) return;
        if (brand) {
          setSelectedBrand(brand);
          setSelectedReference(null);
          setView('brands');
          setShowBrandEdit(true);
        }
      } catch (err) {
        console.error('Failed to load brand for edit modal:', err);
      } finally {
        if (!cancelled) onEditBrandConsumed?.();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editBrandId]);

  // Deep-linked from an AI Sandbox match: fetch the brand + reference fresh (this
  // tab unmounts when inactive, so there's no retained selection) and open the
  // reference editor directly.
  useEffect(() => {
    if (!openReferenceBrandId || !openReferenceId) return;
    let cancelled = false;
    (async () => {
      try {
        const [brand, reference] = await Promise.all([
          getWatchBrandById(openReferenceBrandId),
          getWatchReference(openReferenceBrandId, openReferenceId),
        ]);
        if (cancelled) return;
        if (brand && reference) {
          setSelectedBrand(brand);
          setSelectedReference(reference);
          setVariantParent(null);
          setView('editor');
          window.scrollTo(0, 0);
        } else {
          toast.error('Reference not found');
        }
      } catch (err) {
        console.error('Failed to load reference for editor:', err);
        toast.error('Failed to open reference');
      } finally {
        if (!cancelled) onOpenReferenceConsumed?.();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openReferenceBrandId, openReferenceId]);

  // Save scroll position when entering editor so we can restore it
  const savedScrollY = useRef(0);

  const handleSelectReference = (reference: WatchReference) => {
    savedScrollY.current = window.scrollY;
    setSelectedReference(reference);
    setVariantParent(null);
    setView('editor');
    window.scrollTo(0, 0);
  };

  // Open one of a parent's variants in the editor. Deliberately does NOT touch
  // savedScrollY — that holds the reference LIST's scroll position, which still
  // has to be restored when the admin eventually leaves the editor.
  const handleOpenVariant = (variant: WatchReference) => {
    setVariantParent(selectedReference);
    setSelectedReference(variant);
    window.scrollTo(0, 0);
  };

  // Saving a variant fires the variantAggregates propagation trigger on its
  // parent, so the snapshot taken on the way down is often stale by the time we
  // come back up. Fetch fresh BEFORE swapping the selection: the editor is keyed
  // on the reference id, so it seeds its form state once at mount and a later
  // prop swap would not re-seed it. A failed read falls back to the snapshot.
  const handleBackToVariantParent = async () => {
    if (!variantParent) return;
    let parent = variantParent;
    if (selectedBrand) {
      try {
        const latest = await getWatchReference(selectedBrand.id, variantParent.id);
        if (latest) parent = latest;
      } catch {
        // Transient read failure — the snapshot is still worth showing.
      }
    }
    setSelectedReference(parent);
    setVariantParent(null);
    window.scrollTo(0, 0);
  };

  const handleBackToReferences = () => {
    // Fetch latest reference data so the list reflects all changes (images, tags, etc.)
    if (selectedBrand && selectedReference) {
      getWatchReference(selectedBrand.id, selectedReference.id)
        .then((latest) => {
          if (latest) setLastSavedReference(latest);
        })
        .catch(() => {});
    }
    setSelectedReference(null);
    setVariantParent(null);
    setView('references');
    requestAnimationFrame(() => window.scrollTo(0, savedScrollY.current));
  };

  // Track the latest saved reference so ReferenceBrowser can update its list
  const [lastSavedReference, setLastSavedReference] = useState<WatchReference | null>(null);

  // Track deleted reference ID so ReferenceBrowser can remove it from the list
  const [deletedReferenceId, setDeletedReferenceId] = useState<string | null>(null);

  // Track brand updates from bulk import or other operations
  const [updatedBrand, setUpdatedBrand] = useState<WatchBrand | null>(null);

  const handleBrandUpdated = (brand: WatchBrand) => {
    setSelectedBrand(brand);
    setUpdatedBrand(brand);
  };

  const handleReferenceSaved = (updated: WatchReference) => {
    setSelectedReference(updated);
    setLastSavedReference(updated);
  };

  // Open clone modal with pre-filled values from the source watch
  const handleCloneReference = (cloneData: Partial<WatchReference>) => {
    if (!selectedBrand) return;

    const caseData = cloneData.case as { diameterMm?: number; diameter?: number; material?: string } | undefined;
    const productionData = cloneData.production as { startYear?: number; endYear?: number } | undefined;

    setPendingCloneData(cloneData);
    setCloneReference(''); // Reference should be empty - user needs to enter new one
    setCloneModel(cloneData.model || '');
    setCloneDiameterMm((caseData?.diameterMm || caseData?.diameter || '').toString());
    setCloneCaseMaterial(caseData?.material || '');
    setCloneStartYear((productionData?.startYear || '').toString());
    setCloneEndYear((productionData?.endYear || '').toString());
    setShowCloneModal(true);
  };

  const handleCloseCloneModal = () => {
    setShowCloneModal(false);
    setPendingCloneData(null);
    setCloneReference('');
    setCloneModel('');
    setCloneDiameterMm('');
    setCloneCaseMaterial('');
    setCloneStartYear('');
    setCloneEndYear('');
  };

  const handleConfirmClone = async () => {
    if (!selectedBrand || !pendingCloneData) return;

    // Mirror the service-level invariant: the ref must normalize to a non-empty
    // value (not blank, whitespace-only, or all non-alphanumeric like "---").
    if (!normalizeReference(cloneReference)) {
      toast.error('Reference must contain at least one alphanumeric character');
      return;
    }

    setIsCloning(true);
    try {
      // Build clone data, filtering out undefined values (Firestore rejects undefined)
      const referenceData: Record<string, unknown> = {
        reference: cloneReference.trim(),
        model: cloneModel.trim() || '',
        collection: pendingCloneData.collection || '',
        productName: pendingCloneData.productName || '',
      };

      // Only add optional fields if they have values
      if (pendingCloneData.description) referenceData.description = pendingCloneData.description;
      if (pendingCloneData.calibre) referenceData.calibre = pendingCloneData.calibre;
      if (pendingCloneData.movementType) referenceData.movementType = pendingCloneData.movementType;
      if (pendingCloneData.nickname) referenceData.nickname = pendingCloneData.nickname;
      if (pendingCloneData.specialEdition !== undefined) referenceData.specialEdition = pendingCloneData.specialEdition;
      if (pendingCloneData.series) referenceData.series = pendingCloneData.series;
      if (pendingCloneData.watchType) referenceData.watchType = pendingCloneData.watchType;
      if (pendingCloneData.bezel) referenceData.bezel = pendingCloneData.bezel;
      if (pendingCloneData.bezelType) referenceData.bezelType = pendingCloneData.bezelType;
      if (pendingCloneData.crownType) referenceData.crownType = pendingCloneData.crownType;
      if (pendingCloneData.glass) referenceData.glass = pendingCloneData.glass;
      if (pendingCloneData.crystal) referenceData.crystal = pendingCloneData.crystal;
      if (pendingCloneData.dial) referenceData.dial = pendingCloneData.dial;

      // Apply removeUndefined to nested objects that may contain undefined values
      if (pendingCloneData.movement) referenceData.movement = removeUndefined(pendingCloneData.movement);
      if (pendingCloneData.dialAndHands) referenceData.dialAndHands = removeUndefined(pendingCloneData.dialAndHands);
      if (pendingCloneData.strap) referenceData.strap = removeUndefined(pendingCloneData.strap);
      if (pendingCloneData.electronics) referenceData.electronics = removeUndefined(pendingCloneData.electronics);
      if (pendingCloneData.functions && pendingCloneData.functions.length > 0) referenceData.functions = pendingCloneData.functions;
      if (pendingCloneData.links && pendingCloneData.links.length > 0) referenceData.links = pendingCloneData.links;

      // Build case object with potentially updated values from clone form
      const caseObj: Record<string, unknown> = pendingCloneData.case ? removeUndefined({ ...pendingCloneData.case }) : {};
      if (cloneDiameterMm) {
        const diamVal = parseFloat(cloneDiameterMm);
        caseObj.diameter = diamVal;
        caseObj.diameterMm = diamVal;
        caseObj.sizeMm = diamVal;
      }
      if (cloneCaseMaterial) {
        caseObj.material = cloneCaseMaterial;
      }
      if (Object.keys(caseObj).length > 0) {
        referenceData.case = caseObj;
      }

      // Build production object with potentially updated values from clone form
      const prodObj: Record<string, unknown> = pendingCloneData.production ? removeUndefined({ ...pendingCloneData.production }) : {};
      if (cloneStartYear) {
        prodObj.startYear = parseInt(cloneStartYear);
      }
      if (cloneEndYear) {
        prodObj.endYear = parseInt(cloneEndYear);
      }
      if (Object.keys(prodObj).length > 0) {
        referenceData.production = prodObj;
      }

      // Compute searchText for the cloned reference
      const diameterMm = cloneDiameterMm ? parseFloat(cloneDiameterMm) : undefined;
      referenceData.searchText = computeSearchText(
        selectedBrand.name,
        cloneReference.trim(),
        cloneModel.trim(),
        pendingCloneData.calibre,
        pendingCloneData.movementType,
        diameterMm,
        pendingCloneData.collection,
      );

      // Create the new reference with cloned data
      const newReferenceId = await createWatchReference(selectedBrand.id, referenceData as Omit<WatchReference, 'id' | 'brandId' | 'createdAt' | 'updatedAt'>);

      // Fetch the newly created reference and open it in editor
      const newReference = await getWatchReference(selectedBrand.id, newReferenceId);
      if (newReference) {
        setSelectedReference(newReference);
        toast.success(`Cloned to ${cloneReference.trim()}`);
      }

      handleCloseCloneModal();
    } catch (error) {
      console.error('Error cloning reference:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to clone reference');
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Curated Watches</h1>
          <p className="text-gray-500 mt-1">
            Manage watch brands and references in the database
          </p>
        </div>
        {/* Top-level mount of the global backfill action so it's reachable
            without first drilling into a brand (also lives per-brand in
            ReferenceBrowser). Scans all brands regardless of where it's run. */}
        {view === 'brands' && (
          <div className="flex gap-2 shrink-0">
            <BackfillProductUrls />
          </div>
        )}
      </div>

      {/* Breadcrumb */}
      {(view === 'references' || view === 'editor') && (
        <nav className="flex items-center gap-2 text-sm">
          <button
            onClick={() => guardedExit(handleBackToBrands)}
            className="text-brand-600 hover:underline"
          >
            Brands
          </button>
          {selectedBrand && (
            <>
              <span className="text-gray-400">/</span>
              {view === 'editor' ? (
                <button
                  onClick={() => guardedExit(handleBackToReferences)}
                  className="text-brand-600 hover:underline"
                >
                  {selectedBrand.name}
                </button>
              ) : (
                <span className="text-gray-700">{selectedBrand.name}</span>
              )}
            </>
          )}
          {/* Opened from a parent's Variants panel — keep a way back up. */}
          {variantParent && view === 'editor' && (
            <>
              <span className="text-gray-400">/</span>
              <button
                onClick={() => guardedExit(handleBackToVariantParent)}
                className="text-brand-600 hover:underline"
                title="Back to the parent reference"
              >
                {variantParent.reference}
              </button>
            </>
          )}
          {selectedReference && view === 'editor' && (
            <>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700">{selectedReference.reference}</span>
            </>
          )}
        </nav>
      )}

      {/* Content — BrandEditor stays mounted so list state is preserved */}
      <div style={{ display: view === 'brands' ? undefined : 'none' }}>
        <BrandEditor
          onSelectBrand={handleSelectBrand}
          listState={brandListState}
          onListStateChange={setBrandListState}
          editBrandRequest={showBrandEdit ? selectedBrand : null}
          onEditBrandComplete={(updated) => {
            setShowBrandEdit(false);
            if (updated) setSelectedBrand(updated);
          }}
          updatedBrand={updatedBrand}
          createBrandSeed={createBrandSeed}
          onCreateBrandSeedConsumed={onCreateBrandSeedConsumed}
          onCreateBrandSeedApplied={onCreateBrandSeedApplied}
          editBrandPatchSeed={editBrandPatchSeed}
          onEditBrandPatchSeedConsumed={onEditBrandPatchSeedConsumed}
          onEditBrandPatchSeedApplied={onEditBrandPatchSeedApplied}
          onBrandSeedCancelled={onBrandSeedCancelled}
        />
      </div>

      {/* Keep ReferenceBrowser mounted (hidden) while in editor so state is preserved */}
      {selectedBrand && (view === 'references' || view === 'editor') && (
        <div style={{ display: view === 'references' ? undefined : 'none' }}>
          <ReferenceBrowser
            brand={selectedBrand}
            onBack={handleBackToBrands}
            onSelectReference={handleSelectReference}
            onEditBrand={() => setShowBrandEdit(true)}
            onBrandUpdated={handleBrandUpdated}
            updatedReference={lastSavedReference}
            deletedReferenceId={deletedReferenceId}
            onOpenBulkImport={onOpenBulkImport}
            onOpenExtractorJobs={onOpenExtractorJobs}
            autoRunScraper={autoRunScraper}
            onAutoRunScraperConsumed={onAutoRunScraperConsumed}
          />
        </div>
      )}

      {view === 'editor' && selectedBrand && selectedReference && (
        <ReferenceEditor
          key={selectedReference.id}
          reference={selectedReference}
          brand={selectedBrand}
          exitGuardRef={editorExitGuardRef}
          // Opened from a parent's Variants panel: the editor's back chevron
          // goes up one level to that parent, matching the breadcrumb, instead
          // of dropping straight to the reference list.
          onBack={variantParent ? handleBackToVariantParent : handleBackToReferences}
          onSaved={handleReferenceSaved}
          onClone={handleCloneReference}
          onOpenReference={handleOpenVariant}
          onDeleted={() => {
            if (selectedReference) setDeletedReferenceId(selectedReference.id);
            setSelectedReference(null);
            setView('references');
          }}
        />
      )}

      {/* Clone Modal */}
      <Modal
        isOpen={showCloneModal}
        onClose={handleCloseCloneModal}
        title="Clone Reference"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            These fields are used to generate a unique document ID. Adjust as needed for the cloned watch.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reference <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={cloneReference}
              onChange={(e) => setCloneReference(e.target.value)}
              placeholder="Enter new reference number"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            {cloneReference.length > 0 && !normalizeReference(cloneReference) && (
              <p className="text-xs text-red-500 mt-1">
                Reference must contain at least one alphanumeric character
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model
            </label>
            <input
              type="text"
              value={cloneModel}
              onChange={(e) => setCloneModel(e.target.value)}
              placeholder="Model name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Diameter (mm)
              </label>
              <input
                type="number"
                step="0.1"
                value={cloneDiameterMm}
                onChange={(e) => setCloneDiameterMm(e.target.value)}
                placeholder="e.g., 40"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Case Material
              </label>
              <input
                type="text"
                value={cloneCaseMaterial}
                onChange={(e) => setCloneCaseMaterial(e.target.value)}
                placeholder="e.g., Steel"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Year
              </label>
              <input
                type="number"
                value={cloneStartYear}
                onChange={(e) => setCloneStartYear(e.target.value)}
                placeholder="e.g., 2020"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Year
              </label>
              <input
                type="number"
                value={cloneEndYear}
                onChange={(e) => setCloneEndYear(e.target.value)}
                placeholder="e.g., 2023"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={handleCloseCloneModal}
              disabled={isCloning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmClone}
              loading={isCloning}
              disabled={!normalizeReference(cloneReference)}
            >
              Clone
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
